import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ArrowLeft,
  FileText,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Loader2,
  AlertCircle,
  BookOpen,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSource } from '@/features/sources/hooks/useSources';
import { ChatPanel } from '@/features/chat/components/ChatPanel';
import { supabase } from '@/lib/supabase';

// Configure pdf.js worker — use local bundled worker instead of CDN
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

// =========================================
// Chat page — PDF viewer (left) + Chat panel (right)
// =========================================

export function Chat() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const navigate = useNavigate();
  const { data: source, isLoading, error } = useSource(sourceId);

  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [showText, setShowText] = useState(false);
  const [chatExpanded, setChatExpanded] = useState(false);
  const [highlightedPassage, setHighlightedPassage] = useState<string | null>(null);

  const isPdf = source?.file_type === 'application/pdf' && !!source?.file_path;

  const [pdfUrl, setPdfUrl] = useState<string | undefined>();

  useEffect(() => {
    if (!source?.file_path) return;

    const fetchSignedUrl = () => {
      supabase.storage
        .from('sources')
        .createSignedUrl(source.file_path!, 3600)
        .then(({ data }) => {
          if (data?.signedUrl) setPdfUrl(data.signedUrl);
        });
    };

    fetchSignedUrl();
    // Refresh 5 min before expiry
    const interval = setInterval(fetchSignedUrl, 55 * 60 * 1000);
    return () => clearInterval(interval);
  }, [source?.file_path]);

  const handleDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, numPages)));
  };

  const handleCitationClick = useCallback(
    (passage: { text: string }) => {
      setHighlightedPassage((prev) =>
        prev === passage.text ? null : passage.text
      );
    },
    []
  );

  const customTextRenderer = useCallback(
    (textItem: { str: string }) => {
      if (!highlightedPassage) return textItem.str;
      const words = highlightedPassage
        .split(/\s+/)
        .filter((w) => w.length >= 4)
        .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      if (words.length === 0) return textItem.str;
      const pattern = new RegExp(`(${words.join('|')})`, 'gi');
      return textItem.str.replace(
        pattern,
        '<mark style="background-color:#fef08a;padding:1px 0;border-radius:2px;">$1</mark>'
      );
    },
    [highlightedPassage]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <p className="text-sm text-slate-500">Loading document...</p>
        </div>
      </div>
    );
  }

  // Error / not found
  if (error || !source) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <AlertCircle className="h-10 w-10" />
          <p className="text-sm font-medium text-slate-600">
            {error ? 'Failed to load document' : 'Document not found'}
          </p>
          <Button variant="outline" size="sm" onClick={() => navigate('/sources')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Sources
          </Button>
        </div>
      </div>
    );
  }

  const sourceName = source.title || source.file_name;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => navigate('/sources')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-slate-800 truncate">
              {sourceName}
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              {source.authors && source.authors.length > 0 && (
                <span className="text-[11px] text-slate-400 truncate">
                  {source.authors.slice(0, 2).join(', ')}
                  {source.authors.length > 2 && ' et al.'}
                </span>
              )}
              {source.publication_year && (
                <Badge variant="outline" className="text-[10px] h-4">
                  {source.publication_year}
                </Badge>
              )}
              <Badge
                variant="outline"
                className={cn(
                  'text-[10px] h-4',
                  source.extraction_status === 'completed'
                    ? 'border-emerald-200 text-emerald-600'
                    : 'border-amber-200 text-amber-600'
                )}
              >
                {source.extraction_status}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {isPdf && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showText ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setShowText((v) => !v)}
                >
                  <BookOpen className="h-3.5 w-3.5 mr-1" />
                  Text
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Toggle extracted text view</p>
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setChatExpanded((v) => !v)}
              >
                {chatExpanded ? (
                  <Minimize2 className="h-3.5 w-3.5" />
                ) : (
                  <Maximize2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {chatExpanded ? 'Show document' : 'Expand chat'}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Main content: viewer + chat panel */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Left: Document viewer */}
        {!chatExpanded && (
          <div className="flex-1 flex flex-col overflow-hidden border-r">
            {isPdf && !showText ? (
              <>
                {/* PDF controls */}
                <div className="flex items-center justify-between px-3 py-1.5 border-b bg-slate-50/50 shrink-0">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={currentPage <= 1}
                      onClick={() => goToPage(currentPage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min={1}
                        max={numPages}
                        value={currentPage}
                        onChange={(e) => goToPage(Number(e.target.value))}
                        className="h-7 w-14 text-xs text-center"
                      />
                      <span className="text-xs text-slate-400">/ {numPages}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={currentPage >= numPages}
                      onClick={() => goToPage(currentPage + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={scale <= 0.5}
                      onClick={() => setScale((s) => Math.max(0.5, s - 0.1))}
                    >
                      <ZoomOut className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs text-slate-500 w-10 text-center">
                      {Math.round(scale * 100)}%
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={scale >= 2.5}
                      onClick={() => setScale((s) => Math.min(2.5, s + 0.1))}
                    >
                      <ZoomIn className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* PDF Document */}
                <div className="flex-1 overflow-auto bg-slate-100 flex justify-center p-4">
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
                      </div>
                    }
                    error={
                      <div className="flex flex-col items-center justify-center py-20 gap-2 text-slate-400">
                        <AlertCircle className="h-8 w-8" />
                        <p className="text-sm">Failed to load PDF</p>
                      </div>
                    }
                  >
                    <Page
                      pageNumber={currentPage}
                      scale={scale}
                      className="shadow-lg"
                      customTextRenderer={customTextRenderer}
                    />
                  </Document>
                </div>
              </>
            ) : (
              /* Text viewer (for non-PDF or toggle mode) */
              <div className="flex-1 overflow-auto p-6">
                {source.extracted_text || source.abstract || source.title ? (
                  <div className="max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto space-y-5">
                    {/* Metadata card for sources without a PDF file */}
                    {!source.file_path && (source.title || source.authors?.length || source.doi) && (
                      <div className="bg-white border rounded-lg p-5 space-y-3">
                        {source.title && (
                          <h2 className="text-base font-semibold text-slate-800 leading-snug">
                            {source.title}
                          </h2>
                        )}
                        {source.authors && source.authors.length > 0 && (
                          <p className="text-sm text-slate-500">
                            {source.authors.join(', ')}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          {source.publication_year && (
                            <Badge variant="outline" className="text-xs">
                              {source.publication_year}
                            </Badge>
                          )}
                          {source.journal && (
                            <Badge variant="outline" className="text-xs">
                              {source.journal}
                            </Badge>
                          )}
                          {source.doi && (
                            <a
                              href={`https://doi.org/${source.doi}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:underline"
                            >
                              DOI: {source.doi}
                            </a>
                          )}
                        </div>
                        {source.keywords && source.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {source.keywords.map((kw) => (
                              <Badge key={kw} variant="secondary" className="text-[10px]">
                                {kw}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Abstract section */}
                    {source.abstract && !source.file_path && (
                      <div className="bg-white border rounded-lg p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen className="h-4 w-4 text-slate-400" />
                          <h3 className="text-sm font-medium text-slate-600">Abstract</h3>
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {source.abstract}
                        </p>
                      </div>
                    )}

                    {/* Extracted text */}
                    {source.extracted_text && (source.file_path || !source.abstract) && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <h3 className="text-sm font-medium text-slate-600">
                            Extracted Text
                          </h3>
                          <Badge variant="outline" className="text-[10px] h-4">
                            {source.extracted_text.length.toLocaleString()} chars
                          </Badge>
                        </div>
                        <div className="prose prose-sm prose-slate max-w-none">
                          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed bg-white border rounded-lg p-4">
                            {source.extracted_text}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
                    <FileText className="h-10 w-10 stroke-[1.2]" />
                    <div className="text-center">
                      <p className="text-sm font-medium text-slate-500">
                        No text available
                      </p>
                      <p className="text-xs mt-1">
                        {source.extraction_status === 'pending'
                          ? 'Text extraction is pending...'
                          : source.extraction_status === 'processing'
                            ? 'Text is being extracted...'
                            : 'Text extraction failed for this document.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Right: Chat panel */}
        <div
          className={cn(
            'shrink-0 h-full',
            chatExpanded ? 'w-full' : 'w-full md:w-[400px] lg:w-[450px]'
          )}
        >
          <ChatPanel
            sourceId={sourceId!}
            sourceName={sourceName}
            className="h-full rounded-none border-0"
            highlightedPassage={highlightedPassage ?? undefined}
            onCitationClick={handleCitationClick}
          />
        </div>
      </div>
    </div>
  );
}
